import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { auftraegeApi } from '../lib/auftraege-api';
import {
  MARGE_TYPEN,
  MARGE_TYP_LABEL,
  PROJEKT_STATUS,
  PROJEKT_STATUS_BADGE,
  PROJEKT_STATUS_LABEL,
  projekteApi,
  type MargeTyp,
  type Projekt,
  type ProjektStatus,
} from '../lib/projekte-api';
import { requireAccess } from '../lib/guards';
import { formatGermanDateFromTimestamp } from '../lib/date-format';

export const Route = createFileRoute('/projekte/$id')({
  beforeLoad: () => requireAccess('/api/v1/projekte'),
  component: ProjektDetailPage,
});

function ProjektDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  return <ProjektDetail id={id} />;
}

export function ProjektDetail({ id }: { id: string }): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const projektQ = useQuery({
    queryKey: ['projekte', 'detail', id],
    queryFn: () => projekteApi.getById(id),
  });

  const auftraegeImProjektQ = useQuery({
    queryKey: ['projekte', 'auftraege', id],
    queryFn: () => projekteApi.listAuftraege(id),
  });

  const projekt = projektQ.data?.item;

  const invalidateAll = (): void => {
    void qc.invalidateQueries({ queryKey: ['projekte', 'detail', id] });
    void qc.invalidateQueries({ queryKey: ['projekte', 'auftraege', id] });
    void qc.invalidateQueries({ queryKey: ['projekte', 'list'] });
    void qc.invalidateQueries({ queryKey: ['auftraege', 'list'] });
  };

  const update = useMutation({
    mutationFn: (input: Parameters<typeof projekteApi.update>[1]) => projekteApi.update(id, input),
    onSuccess: invalidateAll,
  });

  const addAuftrag = useMutation({
    mutationFn: (auftragId: string) => projekteApi.addAuftrag(id, auftragId),
    onSuccess: invalidateAll,
  });

  const removeAuftrag = useMutation({
    mutationFn: (auftragId: string) => projekteApi.removeAuftrag(id, auftragId),
    onSuccess: invalidateAll,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title={projekt?.bezeichnung ?? 'Projekt'}
        description={
          projekt
            ? `${projekt.schadennummer ? `Schaden ${projekt.schadennummer} · ` : ''}angelegt ${formatGermanDateFromTimestamp(
                projekt.createdAt,
              )}`
            : '…'
        }
        actions={
          <Button variant="ghost" onClick={() => void navigate({ to: '/projekte' })}>
            ← Zurück
          </Button>
        }
      />

      {projektQ.error ? <ErrorBanner error={projektQ.error as Error} /> : null}
      {update.error ? <ErrorBanner error={update.error as Error} /> : null}
      {addAuftrag.error ? <ErrorBanner error={addAuftrag.error as Error} /> : null}
      {removeAuftrag.error ? <ErrorBanner error={removeAuftrag.error as Error} /> : null}

      {!projekt ? (
        <div className="text-sm text-slate-500">Lädt…</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`rounded-md border px-2 py-1 text-xs font-medium ${PROJEKT_STATUS_BADGE[projekt.status]}`}
            >
              {PROJEKT_STATUS_LABEL[projekt.status]}
            </span>
          </div>

          <StammPanel projekt={projekt} />

          <EditPanel
            projekt={projekt}
            onSave={(input) => update.mutate(input)}
            pending={update.isPending}
          />

          <AuftraegePanel
            projekt={projekt}
            zugeordnet={auftraegeImProjektQ.data?.items ?? []}
            zugeordnetLoading={auftraegeImProjektQ.isLoading}
            zugeordnetError={auftraegeImProjektQ.error as Error | null}
            onAdd={(auftragId) => addAuftrag.mutate(auftragId)}
            onRemove={(auftragId) => removeAuftrag.mutate(auftragId)}
            addPending={addAuftrag.isPending}
            removePending={removeAuftrag.isPending}
          />
        </>
      )}
    </div>
  );
}

function StammPanel({ projekt }: { projekt: Projekt }): JSX.Element {
  return (
    <dl className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2">
      <Field label="Kunde">
        <Link
          to="/kunden/$id"
          params={{ id: projekt.kundeId }}
          className="text-brand-700 hover:underline"
        >
          Kunde öffnen
        </Link>
      </Field>
      <Field label="Objekt">
        {projekt.objektId ? (
          <Link
            to="/objekte/$id"
            params={{ id: projekt.objektId }}
            className="text-brand-700 hover:underline"
          >
            Objekt öffnen
          </Link>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </Field>
      <Field label="Schadennummer">
        {projekt.schadennummer ?? <span className="text-slate-400">—</span>}
      </Field>
      <Field label="Versicherung">
        {projekt.versicherungKundeId ? (
          <Link
            to="/kunden/$id"
            params={{ id: projekt.versicherungKundeId }}
            className="text-brand-700 hover:underline"
          >
            Versicherung öffnen
          </Link>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </Field>
      <Field label="Sachbearbeiter">
        {projekt.sachbearbeiter ?? <span className="text-slate-400">—</span>}
      </Field>
      <Field label="Marge">
        {projekt.margeTyp && projekt.margeWert ? (
          `${projekt.margeWert} ${projekt.margeTyp === 'aufschlag' ? '%' : 'EUR'} (${MARGE_TYP_LABEL[projekt.margeTyp]})`
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </Field>
    </dl>
  );
}

function EditPanel({
  projekt,
  onSave,
  pending,
}: {
  projekt: Projekt;
  onSave: (input: { status?: ProjektStatus; margeTyp?: MargeTyp; margeWert?: string }) => void;
  pending: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<ProjektStatus>(projekt.status);
  const [margeTyp, setMargeTyp] = useState<'' | MargeTyp>(projekt.margeTyp ?? '');
  const [margeWert, setMargeWert] = useState<string>(projekt.margeWert ?? '');

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-medium text-slate-700">Status & Marge</div>
        <Button variant="ghost" onClick={() => setOpen((v) => !v)} disabled={pending}>
          {open ? 'Abbrechen' : 'Bearbeiten'}
        </Button>
      </div>
      {open ? (
        <div className="space-y-3 border-t border-slate-200 pt-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Status</span>
            <Select value={status} onChange={(e) => setStatus(e.target.value as ProjektStatus)}>
              {PROJEKT_STATUS.map((s) => (
                <option key={s} value={s}>
                  {PROJEKT_STATUS_LABEL[s]}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Marge-Typ</span>
            <Select value={margeTyp} onChange={(e) => setMargeTyp(e.target.value as '' | MargeTyp)}>
              <option value="">– Keine –</option>
              {MARGE_TYPEN.map((t) => (
                <option key={t} value={t}>
                  {MARGE_TYP_LABEL[t]}
                </option>
              ))}
            </Select>
          </label>
          {margeTyp ? (
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">
                Marge-Wert ({margeTyp === 'aufschlag' ? '%' : 'EUR'})
              </span>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={margeWert}
                onChange={(e) => setMargeWert(e.target.value)}
                placeholder={margeTyp === 'aufschlag' ? 'z. B. 15' : 'z. B. 250.00'}
              />
            </label>
          ) : null}
          <Button
            onClick={() => {
              const input: { status?: ProjektStatus; margeTyp?: MargeTyp; margeWert?: string } = {};
              if (status !== projekt.status) input.status = status;
              if (
                margeTyp !== '' &&
                (margeTyp !== projekt.margeTyp || margeWert !== projekt.margeWert)
              ) {
                input.margeTyp = margeTyp;
                if (margeWert.trim().length > 0) input.margeWert = margeWert.trim();
              }
              if (Object.keys(input).length > 0) onSave(input);
              setOpen(false);
            }}
            disabled={pending}
          >
            {pending ? 'Speichere…' : 'Speichern'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function AuftraegePanel({
  projekt,
  zugeordnet,
  zugeordnetLoading,
  zugeordnetError,
  onAdd,
  onRemove,
  addPending,
  removePending,
}: {
  projekt: Projekt;
  zugeordnet: Array<{ id: string; titel: string }>;
  zugeordnetLoading: boolean;
  zugeordnetError: Error | null;
  onAdd: (auftragId: string) => void;
  onRemove: (auftragId: string) => void;
  addPending: boolean;
  removePending: boolean;
}): JSX.Element {
  const [selectedAuftragId, setSelectedAuftragId] = useState('');

  /**
   * Anhängbare Aufträge: gleicher Kunde wie das Projekt UND noch keinem
   * Projekt zugeordnet. Backend wirft sonst PROJEKT_AUFTRAG_MISMATCH (422).
   * Filterung clientseitig, damit der CEO im Dropdown nur valide Optionen
   * sieht.
   */
  const auftraegeQ = useQuery({
    queryKey: ['auftraege', 'list'],
    queryFn: () => auftraegeApi.list(),
  });
  const candidates = useMemo(() => {
    return (auftraegeQ.data?.items ?? []).filter(
      (a) => a.kundeId === projekt.kundeId && a.projektId === null,
    );
  }, [auftraegeQ.data, projekt.kundeId]);

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-sm font-medium text-slate-700">Aufträge im Projekt</div>

      {zugeordnetError ? <ErrorBanner error={zugeordnetError} /> : null}
      {auftraegeQ.error ? <ErrorBanner error={auftraegeQ.error as Error} /> : null}

      {zugeordnetLoading ? (
        <div className="text-sm text-slate-500">Lädt…</div>
      ) : zugeordnet.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
          Noch keine Aufträge zugeordnet.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
          {zugeordnet.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <Link
                to="/auftraege/$id"
                params={{ id: a.id }}
                className="min-w-0 flex-1 truncate text-sm text-brand-700 hover:underline"
              >
                {a.titel}
              </Link>
              <Button
                variant="ghost"
                onClick={() => onRemove(a.id)}
                disabled={removePending || addPending}
              >
                Entfernen
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2 border-t border-slate-200 pt-3">
        <label className="min-w-[240px] flex-1">
          <span className="mb-1 block text-xs font-medium text-slate-600">
            Auftrag hinzufügen (nur unzugeordnete Aufträge desselben Kunden)
          </span>
          <Select value={selectedAuftragId} onChange={(e) => setSelectedAuftragId(e.target.value)}>
            <option value="">– Auftrag wählen –</option>
            {candidates.map((a) => (
              <option key={a.id} value={a.id}>
                {a.titel}
              </option>
            ))}
          </Select>
        </label>
        <Button
          onClick={() => {
            if (selectedAuftragId.length > 0) {
              onAdd(selectedAuftragId);
              setSelectedAuftragId('');
            }
          }}
          disabled={selectedAuftragId.length === 0 || addPending || removePending}
        >
          {addPending ? 'Speichere…' : 'Hinzufügen'}
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}): JSX.Element {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-900 whitespace-pre-wrap">{children}</dd>
    </div>
  );
}
